/**
 * Notifications page — full history of every in-app notification for
 * the current user. Supports read/unread filter, mark-all-read and
 * click-through to the source entity via NOTIFICATION_KINDS.linkFor.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, CheckCircle2, Loader2 } from "lucide-react";
import { NOTIFICATION_KINDS } from "@/components/notifications/kinds";


const KIND_LABELS = {
  task_assigned:         "Task assigned",
  task_commented:        "Task comment",
  task_mention:          "@mention",
  procedure_published:   "Procedure",
  training_assigned:     "Training",
  training_module_published: "New training package",
  qualification_expiry:  "Qualification expiry",
  announcement_published:"Announcement",
  enquiry_assigned:      "Enquiry",
  enquiry_follow_up_due: "Follow-up due",
  checkin_alert:         "Progress review alert",
};


export default function Notifications() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all"); // 'all' | 'unread'
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const r = await api.get("/notifications", {
        params: {
          limit: 200,
          unread_only: filter === "unread" ? true : undefined,
        },
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
      setRows([]);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  async function markAllRead() {
    setBusy(true);
    try {
      const r = await api.post("/notifications/mark-all-read", {});
      toast.success(`Marked ${r.data?.marked || 0} as read`);
      load();
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }

  async function markOne(n) {
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.id}/read`, {}); }
      catch { /* ignore */ }
    }
    const target = NOTIFICATION_KINDS[n.kind]?.linkFor?.(n) || null;
    if (target) nav(target);
    else load();
  }

  const unreadCount = (rows || []).filter((r) => !r.read_at).length;

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4" data-testid="notifications-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-950 flex items-center gap-2">
            <Bell className="h-6 w-6" /> Notifications
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">
            Everything that mentions you — task comments, procedure publishes,
            training assignments, and qualification reminders.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-zinc-300 overflow-hidden">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 h-8 text-xs ${filter === "all" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
              data-testid="notifications-filter-all"
            >All</button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-3 h-8 text-xs border-l border-zinc-300 ${filter === "unread" ? "bg-zinc-950 text-white" : "bg-white text-zinc-700"}`}
              data-testid="notifications-filter-unread"
            >Unread{unreadCount ? ` (${unreadCount})` : ""}</button>
          </div>
          <Button
            variant="outline"
            onClick={markAllRead}
            disabled={busy || !rows || unreadCount === 0}
            className="text-xs"
            data-testid="notifications-mark-all-read"
          >
            {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Working…</> : <><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark all read</>}
          </Button>
        </div>
      </div>

      {rows === null ? (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-10 rounded-md shadow-none border-2 border-dashed border-zinc-200 bg-zinc-50 text-center text-sm text-zinc-500">
          {filter === "unread"
            ? "Everything read — nice."
            : "No notifications yet."}
        </Card>
      ) : (
        <Card className="rounded-md shadow-none border border-zinc-200 divide-y divide-zinc-100" data-testid="notifications-list">
          {rows.map((n) => {
            const meta = NOTIFICATION_KINDS[n.kind] || NOTIFICATION_KINDS.default;
            const Icon = meta.icon;
            const label = KIND_LABELS[n.kind] || n.kind;
            return (
              <button
                key={n.id}
                onClick={() => markOne(n)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-zinc-50 transition-colors ${n.read_at ? "opacity-70" : ""}`}
                data-testid={`notification-${n.id}`}
              >
                <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${meta.tone}`} strokeWidth={2} />
                <div className="min-w-0 flex-1">
                  <div className="text-2xs uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
                  <div className="text-sm text-zinc-900 mt-0.5">{n.message}</div>
                  <div className="text-2xs text-zinc-500 mt-1">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                {!n.read_at && (
                  <span className="h-2 w-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />
                )}
              </button>
            );
          })}
        </Card>
      )}
    </div>
  );
}
